/**
 * @file The reply-to choices one user has under the org's `emailSenderPolicy`
 * (SYD-2741): the functional accounts an admin listed them on, their default
 * choice, the choice that applies to a `requested` pick, and whether the sender
 * gets a Cc copy. The send modal and the workflow send sidebar build their Reply
 * to options from it; the `sendEmail` callable and `executeWorkflow` re-derive
 * `choice` server-side, so a client never replies from an account it is not
 * listed on. Both a default and a request naming an account the user is not on
 * fall back the same way: to the policy `default`, and a request falls back to
 * the user's default. With `allowUserChoice: false` the request is ignored.
 * `copySender` is fixed by `choice` for System email (always) and My email
 * (never), which `copySenderFixed` reports; only under a functional account does
 * `requestedCopySender` decide it, and its absence means a copy.
 */
import type { FunctionalAccount, OrganizationParameters, ReplyToChoice } from './System.Parameters.types';

export function replyToChoicesFor(
    policy: OrganizationParameters['emailSenderPolicy'],
    userId: string | undefined,
    requested?: ReplyToChoice,
    requestedCopySender?: boolean,
): {
    functionalAccounts: FunctionalAccount[];
    defaultChoice: ReplyToChoice;
    choice: ReplyToChoice;
    copySender: boolean;
    copySenderFixed: boolean;
} {
    const functionalAccounts = userId
        ? (policy?.functionalAccounts ?? []).filter((account) => account.userIds.includes(userId))
        : [];
    const permits = (candidate: ReplyToChoice | undefined): candidate is ReplyToChoice =>
        candidate === 'system'
        || candidate === 'user'
        || functionalAccounts.some((account) => account.email === candidate);
    const userDefault = userId ? policy?.userDefaultReplyTo?.[userId] : undefined;
    const defaultChoice = permits(userDefault) ? userDefault : (policy?.default ?? 'system');
    const choice = policy?.allowUserChoice !== false && permits(requested) ? requested : defaultChoice;
    const copySenderFixed = choice === 'system' || choice === 'user';
    const copySender = copySenderFixed ? choice === 'system' : requestedCopySender !== false;
    return { functionalAccounts, defaultChoice, choice, copySender, copySenderFixed };
}
